import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle, ExternalLink, Save, Upload, Trash2, Power } from "lucide-react";
import myAxios from "../AxiosUrl";
import uploadService from "../../services/uploadService";
import Loading from "../Loading/Loading";
import styles from "./SponsorSettings.module.css";

const MAX_SLOTS = 3;
const SPONSOR_ROTATION_INTERVAL = 6;

const emptySponsorSlot = (slot) => ({
  slot,
  name: "",
  websiteUrl: "",
  feedImageUrl: "",
});

const normalizeUrl = (url) => {
  const trimmed = String(url || "").trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const settingMetadata = {
  sponsor_enabled: {
    title: "Sponsor System Enabled",
    description: "Controls visibility of sponsor data in mobile clients",
  },
  sponsor_rotation_interval: {
    title: "Sponsor Rotation Interval",
    description: "Inject one sponsor feed item after N normal feed messages",
  },
  sponsor_main_name: {
    title: "Main Sponsor Name",
    description: "Main sponsor label displayed at top of app",
  },
  sponsor_main_url: {
    title: "Main Sponsor Website URL",
    description: "Tap destination for the main sponsor label",
  },
  sponsor_feed_1_name: {
    title: "Feed Sponsor 1 Name",
    description: "Optional display name for feed sponsor slot 1",
  },
  sponsor_feed_1_url: {
    title: "Feed Sponsor 1 Website URL",
    description: "Tap destination for sponsor slot 1",
  },
  sponsor_feed_1_image: {
    title: "Feed Sponsor 1 Image",
    description: "Feed-card image URL for sponsor slot 1",
  },
  sponsor_feed_2_name: {
    title: "Feed Sponsor 2 Name",
    description: "Optional display name for feed sponsor slot 2",
  },
  sponsor_feed_2_url: {
    title: "Feed Sponsor 2 Website URL",
    description: "Tap destination for sponsor slot 2",
  },
  sponsor_feed_2_image: {
    title: "Feed Sponsor 2 Image",
    description: "Feed-card image URL for sponsor slot 2",
  },
  sponsor_feed_3_name: {
    title: "Feed Sponsor 3 Name",
    description: "Optional display name for feed sponsor slot 3",
  },
  sponsor_feed_3_url: {
    title: "Feed Sponsor 3 Website URL",
    description: "Tap destination for sponsor slot 3",
  },
  sponsor_feed_3_image: {
    title: "Feed Sponsor 3 Image",
    description: "Feed-card image URL for sponsor slot 3",
  },
};

const SponsorSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});

  const [sponsorEnabled, setSponsorEnabled] = useState(true);
  const [feedSponsorEnabled, setFeedSponsorEnabled] = useState(true);
  const [feedSlotEnabled, setFeedSlotEnabled] = useState({ 1: true, 2: true, 3: true });
  const [form, setForm] = useState({
    mainSponsorName: "",
    mainSponsorUrl: "",
    feedSponsors: [
      emptySponsorSlot(1),
      emptySponsorSlot(2),
      emptySponsorSlot(3),
    ],
  });

  const activeFeedSponsorCount = useMemo(
    () =>
      form.feedSponsors.filter(
        (slot) => slot.feedImageUrl && slot.websiteUrl
      ).length,
    [form.feedSponsors]
  );

  useEffect(() => {
    fetchSponsorSettings();
  }, []);

  const fetchSponsorSettings = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await myAxios.get("/settings/category/sponsor");
      const settings = response?.data?.data || [];
      const map = settings.reduce((acc, setting) => {
        acc[setting.key] = setting.value;
        return acc;
      }, {});

      setSponsorEnabled(map.sponsor_enabled !== false);
      setFeedSponsorEnabled(map.sponsor_feed_enabled !== false);
      setFeedSlotEnabled({
        1: map.sponsor_feed_1_enabled !== false,
        2: map.sponsor_feed_2_enabled !== false,
        3: map.sponsor_feed_3_enabled !== false,
      });
      setForm({
        mainSponsorName: map.sponsor_main_name || "",
        mainSponsorUrl: map.sponsor_main_url || "",
        feedSponsors: [1, 2, 3].map((slot) => ({
          slot,
          name: map[`sponsor_feed_${slot}_name`] || "",
          websiteUrl: map[`sponsor_feed_${slot}_url`] || "",
          feedImageUrl: map[`sponsor_feed_${slot}_image`] || "",
        })),
      });
    } catch (fetchError) {
      console.error("Failed to fetch sponsor settings:", fetchError);
      setError("Failed to load sponsor settings");
    } finally {
      setLoading(false);
    }
  };

  const updateSlotField = (slotIndex, field, value) => {
    setForm((prev) => ({
      ...prev,
      feedSponsors: prev.feedSponsors.map((slot, index) =>
        index === slotIndex
          ? {
              ...slot,
              [field]: value,
            }
          : slot
      ),
    }));
  };

  const upsertSettingByKey = async (key, value) => {
    try {
      await myAxios.put(`/settings/key/${key}`, { value });
      return;
    } catch (updateError) {
      if (updateError?.response?.status !== 404) {
        throw updateError;
      }
    }

    const metadata = settingMetadata[key] || {
      title: key,
      description: "Sponsor setting",
    };

    await myAxios.post("/settings/mixed", {
      key,
      value,
      category: "sponsor",
      title: metadata.title,
      description: metadata.description,
      isEditable: true,
      isRequired: false,
    });
  };

  const handleUpload = async (slotIndex, file) => {
    if (!file) return;

    try {
      setUploadingSlot(slotIndex);
      setError("");

      const extension = file.name.split(".").pop() || "png";
      const filename = `sponsor-feed-${slotIndex + 1}.${extension}`;

      const uploadResult = await uploadService.uploadToCDN(
        file,
        (progress) => {
          setUploadProgress((prev) => ({
            ...prev,
            [slotIndex]: progress,
          }));
        },
        filename
      );

      if (!uploadResult.success || !uploadResult.url) {
        throw new Error(uploadResult.error || "Upload failed");
      }

      updateSlotField(slotIndex, "feedImageUrl", uploadResult.url);
      setSuccessMessage(`Slot ${slotIndex + 1} image uploaded`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (uploadError) {
      console.error("Sponsor upload failed:", uploadError);
      setError(uploadError.message || "Image upload failed");
    } finally {
      setUploadingSlot(null);
      setUploadProgress((prev) => ({
        ...prev,
        [slotIndex]: 0,
      }));
    }
  };

  const handleClearSlot = (slotIndex) => {
    updateSlotField(slotIndex, "name", "");
    updateSlotField(slotIndex, "websiteUrl", "");
    updateSlotField(slotIndex, "feedImageUrl", "");
  };

  const handleClearAll = () => {
    if (!window.confirm("Are you sure? This will delete all sponsors.")) {
      return;
    }
    setForm({
      mainSponsorName: "",
      mainSponsorUrl: "",
      feedSponsors: [
        emptySponsorSlot(1),
        emptySponsorSlot(2),
        emptySponsorSlot(3),
      ],
    });
  };

  const handleToggleSponsor = () => {
    setSponsorEnabled(!sponsorEnabled);
  };

  const handleToggleFeedSponsors = () => {
    setFeedSponsorEnabled(!feedSponsorEnabled);
  };

  const handleToggleSlot = (slot) => {
    setFeedSlotEnabled((prev) => ({
      ...prev,
      [slot]: !prev[slot],
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");

      const normalizedMainUrl = normalizeUrl(form.mainSponsorUrl);
      const feedSlots = form.feedSponsors.map((slot) => ({
        ...slot,
        websiteUrl: normalizeUrl(slot.websiteUrl),
      }));

      for (let i = 0; i < feedSlots.length; i += 1) {
        const slot = feedSlots[i];
        const hasAnyField = Boolean(
          slot.name?.trim() || slot.websiteUrl || slot.feedImageUrl
        );

        if (!hasAnyField) continue;

        if (!slot.feedImageUrl) {
          throw new Error(`Feed sponsor ${i + 1}: image is required`);
        }

        if (!slot.websiteUrl) {
          throw new Error(`Feed sponsor ${i + 1}: website URL is required`);
        }
      }

      const updates = [
        ["sponsor_enabled", sponsorEnabled],
        ["sponsor_feed_enabled", feedSponsorEnabled],
        ["sponsor_rotation_interval", SPONSOR_ROTATION_INTERVAL],
        ["sponsor_main_name", form.mainSponsorName.trim()],
        ["sponsor_main_url", normalizedMainUrl],
      ];

      feedSlots.forEach((slot) => {
        updates.push([`sponsor_feed_${slot.slot}_enabled`, feedSlotEnabled[slot.slot] !== false]);
        updates.push([`sponsor_feed_${slot.slot}_name`, slot.name.trim()]);
        updates.push([`sponsor_feed_${slot.slot}_url`, slot.websiteUrl]);
        updates.push([`sponsor_feed_${slot.slot}_image`, slot.feedImageUrl]);
      });

      for (const [key, value] of updates) {
        await upsertSettingByKey(key, value);
      }

      setSuccessMessage("Sponsor settings saved successfully");
      setTimeout(() => setSuccessMessage(""), 4000);
      await fetchSponsorSettings();
    } catch (saveError) {
      console.error("Failed to save sponsor settings:", saveError);
      setError(saveError.message || "Failed to save sponsor settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className={styles.container}>
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        className={styles.header}
      >
        <div className={styles.headerIcon}>
          <span className="material-icons">campaign</span>
        </div>
        <div>
          <h1>Sponsor Settings</h1>
          <p>
            Manage persistent main sponsor and up to three rotating feed sponsors.
          </p>
        </div>
      </motion.div>

      {successMessage && (
        <div className={`${styles.alert} ${styles.success}`}>
          <CheckCircle size={18} />
          <span>{successMessage}</span>
        </div>
      )}

      {error && (
        <div className={`${styles.alert} ${styles.error}`}>
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitleRow}>
            <span className="material-icons">flag</span>
            <h2>Main Sponsor</h2>
          </div>
          <button
            type="button"
            className={`${styles.toggleButton} ${sponsorEnabled ? styles.toggleOn : styles.toggleOff}`}
            onClick={handleToggleSponsor}
            title={sponsorEnabled ? "Disable sponsor system" : "Enable sponsor system"}
          >
            <Power size={16} />
            <span>{sponsorEnabled ? "Enabled" : "Disabled"}</span>
          </button>
        </div>
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Sponsor Name</span>
            <input
              type="text"
              value={form.mainSponsorName}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  mainSponsorName: event.target.value,
                }))
              }
              placeholder="McDonald's"
            />
          </label>

          <label className={styles.field}>
            <span>Sponsor Website URL</span>
            <input
              type="text"
              value={form.mainSponsorUrl}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  mainSponsorUrl: event.target.value,
                }))
              }
              placeholder="https://example.com"
            />
          </label>

          <div className={styles.mainSponsorPreview}>
            <span className={styles.previewLabel}>Preview</span>
            <div className={styles.previewCard}>
              <strong>Sponsor:</strong>
              <span>{form.mainSponsorName?.trim() || "Not configured"}</span>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitleRow}>
            <span className="material-icons">view_stream</span>
            <h2>Additional Feed Sponsors</h2>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={`${styles.toggleButton} ${feedSponsorEnabled ? styles.toggleOn : styles.toggleOff}`}
              onClick={handleToggleFeedSponsors}
              title={feedSponsorEnabled ? "Disable feed sponsors" : "Enable feed sponsors"}
            >
              <Power size={16} />
              <span>{feedSponsorEnabled ? "Enabled" : "Disabled"}</span>
            </button>
            <span>{activeFeedSponsorCount} active of {MAX_SLOTS} slots</span>
            {activeFeedSponsorCount > 0 && (
              <button
                type="button"
                className={styles.clearAllButton}
                onClick={handleClearAll}
                title="Delete all sponsors"
              >
                <Trash2 size={14} />
                Clear All
              </button>
            )}
          </div>
        </div>

        <div className={styles.slotsGrid}>
          {form.feedSponsors.map((slot, index) => (
            <article key={slot.slot} className={styles.slotCard}>
              <div className={styles.slotHeader}>
                <h3>Sponsor Slot {slot.slot}</h3>
                <div className={styles.slotActions}>
                  {slot.name && slot.websiteUrl && slot.feedImageUrl ? (
                    <button
                      type="button"
                      className={`${styles.slotToggleButton} ${feedSlotEnabled[slot.slot] ? styles.toggleOn : styles.toggleOff}`}
                      onClick={() => handleToggleSlot(slot.slot)}
                      title={feedSlotEnabled[slot.slot] ? "Disable this sponsor" : "Enable this sponsor"}
                    >
                      <Power size={14} />
                      <span>{feedSlotEnabled[slot.slot] ? "On" : "Off"}</span>
                    </button>
                  ) : null}
                  {(slot.name || slot.websiteUrl || slot.feedImageUrl) && (
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => handleClearSlot(index)}
                      title="Delete this sponsor"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              <label className={styles.field}>
                <span>Sponsor Name</span>
                <input
                  type="text"
                  value={slot.name}
                  onChange={(event) =>
                    updateSlotField(index, "name", event.target.value)
                  }
                  placeholder={`Sponsor ${slot.slot}`}
                />
              </label>

              <label className={styles.field}>
                <span>Website URL</span>
                <input
                  type="text"
                  value={slot.websiteUrl}
                  onChange={(event) =>
                    updateSlotField(index, "websiteUrl", event.target.value)
                  }
                  placeholder="https://example.com"
                />
              </label>

              <div className={styles.field}>
                <span>Feed Image</span>
                <div className={styles.feedImageFrame}>
                  {slot.feedImageUrl ? (
                    <img
                      src={slot.feedImageUrl}
                      alt={`Sponsor slot ${slot.slot}`}
                      className={styles.feedImagePreview}
                    />
                  ) : (
                    <div className={styles.feedImagePlaceholder}>
                      <span className="material-icons">image</span>
                      <span>Upload feed-format image</span>
                    </div>
                  )}
                </div>

                {slot.feedImageUrl ? (
                  <a
                    href={slot.feedImageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.previewLink}
                  >
                    <ExternalLink size={14} />
                    Open original image
                  </a>
                ) : null}

                <label className={styles.uploadButton}>
                  <Upload size={16} />
                  <span>
                    {uploadingSlot === index
                      ? `Uploading ${uploadProgress[index] || 0}%`
                      : "Upload Feed Image"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => handleUpload(index, event.target.files?.[0])}
                    disabled={uploadingSlot === index}
                  />
                </label>

                <span className={styles.helperText}>
                  Recommended: same dimensions as Base feed cards. Feed sponsors appear after every sixth normal post.
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.saveButton}
          onClick={handleSave}
          disabled={saving}
        >
          <Save size={16} />
          <span>{saving ? "Saving..." : "Save Sponsor Settings"}</span>
        </button>
      </div>
    </div>
  );
};

export default SponsorSettings;
